// v1.7.39.71 — CRM/Opportunity Management regroup (real decision logged
//   2026-07-27). "Sales & CRM" -> "CRM" (Contacts/Companies/Tasks/Outreach,
//   Shane's call: task management and outreach are core CRM functions, not
//   a separate bucket). "Quoting & Orders" -> "Opportunity Management"
//   (Product Catalog/Quotes/Sales Orders, plus Pipeline once that module
//   gets built). Business Development unchanged. Fixed a real double-header
//   collision this created: the crm item's own label was also "CRM", same
//   as its new group name -- renamed the item to "Contacts & Companies",
//   same fix pattern already used for Storage and Partners.
// v1.7.38 — moduleRegistry.js (Item #NAV-CLEANUP)
// Built from Shane's direct paste of the confirmed 7/13 known-good file,
// plus the verified real Settings filenames pulled directly from the
// github.dev file-tree screenshot (About.jsx, CalendarConnections.jsx,
// CustomProperties.jsx, DataHealth.jsx, EmailConnections.jsx,
// Integrations.jsx, OpsAudit.jsx, PartnerProfile.jsx, PartnersManager.jsx,
// ReplyParserSettings.jsx, SignaturesManager.jsx, SystemHealth.jsx,
// UsersAdmin.jsx) — confirmed against an actual folder listing, not
// guessed. Earlier attempts this week fabricated a TeamMembers.jsx file
// that does not exist; this version does not repeat that mistake.
//
// Exactly three changes from the known-good baseline:
//   1. Meeting Scout, Scout Tracker, Apollo Search get `hidden: true` —
//      real routes, unchanged otherwise, just off the main sidebar.
//      Reachable via the HUNT hub (HuntLauncher.jsx, already shipped).
//   2. Data Health and Apollo Settings (mapped to Integrations.jsx —
//      flagged below, needs Shane's confirmation) removed from their
//      duplicate spot under Business Development. Exist once, under
//      Settings > System & Data.
//   3. Settings restructured from 13 flat entries into 4 grouped
//      clusters (Connections, Partners, System & Data, Account), same
//      subItems pattern already used for CRM/Outreach.
//
// v1.7.38 #DBL-HEADER-FIX (2026-07-22):
//   4. Storage item label renamed "Browse Files" (was "Storage") —
//      group header and item label were identical, causing double render.
//   5. settings-partners cluster label renamed "Partner Management"
//      (was "Partners") — cluster label and first subItem label were
//      identical, causing double render.
//
// FLAG FOR SHANE: "Apollo Settings" is mapped to Integrations.jsx below
// based on the commit message ("v0.98.1 — Apollo Search polish sweep"),
// but the filename itself doesn't confirm this is exclusively an Apollo
// settings page vs. a broader integrations screen. Confirm this mapping
// is right, or tell me the real Apollo Settings filename if it's
// something else.
//
// Please diff-check this against your real file before committing, same
// standing caution as always for this specific file.

import { withCrmPage } from '../modules/crm/CrmPage';
import { lazy } from 'react';

// v1.7.39.192 — real icon set, first batch. Only the 5 top-level, non-hidden
// entries with a real single icon slot get wired this pass (crm, repd-launcher,
// reports, tech-sheets, storage). whistle-hunt/targets/apollo-search are
// hidden:true (real entry point is HuntLauncher.jsx, wired separately).
// the-ledger and enrichment-house are subItems -- that schema has no icon
// slot at all yet, real, separate follow-up. compass and goldmine have no
// nav destination (score badge / background service).
import iconRepd from '../assets/icons/repd.png';
import iconReports from '../assets/icons/reports.png';
import iconTechSheets from '../assets/icons/tech-sheets.png';
import iconStorage from '../assets/icons/storage.png';
import iconHunt from '../assets/icons/hunt.png';
import iconSettings from '../assets/icons/settings.png';
import iconCrm from '../assets/icons/crm.png';
import iconLobby from '../assets/icons/lobby.png';
import iconGoldmine from '../assets/icons/goldmine.png';
import iconPipeline from '../assets/icons/pipeline.png';

export const MODULE_REGISTRY = [
  {
    // Text a Contact: the phone Text screen as a page.
    key: 'text-contact',
    label: 'Text a Contact',
    icon: 'ICONS.search',
    hidden: true,
    component: lazy(() => import('../modules/home/TextContact')),
  },
  {
    // Email a Contact: search, then Email opens the composer.
    key: 'email-contact',
    label: 'Email a Contact',
    icon: 'ICONS.search',
    hidden: true,
    component: lazy(() => import('../modules/home/EmailContact')),
  },
  {
    // CRM lookup: search contacts or companies.
    key: 'crm-lookup',
    label: 'CRM Lookup',
    icon: 'ICONS.search',
    hidden: true,
    component: lazy(() => import('../modules/home/CrmLookup')),
  },
  {
    // v1.7.39.207 -- real, Shane's confirmation 2026-08-04: this item
    // already navigates straight to the real personal dashboard on
    // click (see App.js, mod.key === 'dashboard' forces
    // viewMode='personal', a real behavior locked since v1.7.39.150).
    // Icon still the inert 'ICONS.dashboard' placeholder here in the
    // static registry -- real per-user icon override lives in App.js
    // (SIDEBAR_INITIAL_ICONS), not here, since it needs to know who's
    // actually logged in. Real, updated 2026-08-04: label reverted to
    // plain "Dashboard" -- Shane's call, the per-user icon (S/A/M/N)
    // already carries the personalization visually, "My" was
    // redundant once the icon reads as a real initial.
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'ICONS.dashboard',
    sectionLabel: 'COMMAND', // WO-0492 Phase 1 -- COMMAND cluster (Dashboard, Hangar), A51 adjudicated 2026-09-14, supersedes prior #1797 'Dashboard stays ungrouped' decision
    component: lazy(() => import('../modules/dashboard/Dashboard')),
    // Hangar and Focus are their own top-level items (Focus directly under Dashboard), not children of it.
  },
  {
    // Focus: always on the left menu, directly under Dashboard, with its own icon (Shane: "Focus needs to be on the left menu alone, not collapsed").
    key: 'focus-board',
    label: 'Focus',
    icon: 'M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-2.202.55V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z',
    component: lazy(() => import('../modules/focus/FocusBoard')),
  },
  {
    key: 'mobile-capture',
    label: 'Mobile capture',
    icon: 'ICONS.search',
    hidden: true,
    component: lazy(() => import('../modules/home/MobileCapture')),
  },
  {
    // Ledger #1107 -- Alyssa's quota / focus dashboard. Sits beside
    // Dashboard rather than inside it: this answers "what should I work
    // on today" for a specific person, which is a genuinely different
    // question from Dashboard's "how is everything doing".
    // Ledger #1611 -- where the 20 archetype intake definitions get
    // written. Sits in Settings because it is configuration, not daily
    // work: defining an archetype happens once per partner at onboarding.
    // Ledger #1571 -- The Hangar. Consolidates four data-quality tools
    // that currently live in three different places. Stinger: "scattered
    // makes me nervous."
    // #1797 -- moved into Dashboard's accordion as a real subItem
    // (Stinger direct 2026-08-23). Entry stays, hidden, purely so
    // buildRouteMap() keeps resolving the real component.
    // #2049 -- real, promoted back to a genuine top-level nav item
    // (hidden:true removed) per Goose/Stinger direct 2026-09-07 -- see
    // the comment on Dashboard's subItems above for the full context.
    // Sits right after the Dashboard block, same position it occupied
    // visually while folded inside it -- least disruptive placement,
    // not a new information-architecture decision (that's #2043's
    // scope, audit-only, not touched here).
    // WO-0492 Phase 1 patch (2026-09-15) -- real fix, confirmed by
    // Iceman: 'ICONS.settings' was a literal placeholder string with
    // no ICONS lookup object anywhere in the codebase -- fell through
    // to App.js's raw <path d={mod.icon}/> fallback (Hangar has no
    // entry in SIDEBAR_NAV_ICONS), rendering as a broken SVG path,
    // real live console error. Replaced with a real, valid Heroicons
    // "wrench" solid path (MIT license, single fill path, matches the
    // existing fallback's <svg fill={color}><path/></svg> shape
    // exactly) as a real placeholder pending dedicated Hangar artwork
    // -- not blocking, but no longer broken.
    key: 'hangar',
    label: 'The Hangar',
    icon: 'M12 6.75a5.25 5.25 0 016.775-5.025.75.75 0 01.313 1.248l-3.32 3.319a2.248 2.248 0 001.941 1.939l3.318-3.319a.75.75 0 011.248.313 5.25 5.25 0 01-5.472 6.756c-1.018-.086-1.87.1-2.309.634L7.344 21.3A3.298 3.298 0 112.7 16.657l8.684-7.151c.533-.44.72-1.291.634-2.309A5.342 5.342 0 0112 6.75ZM4.117 19.125a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.008a.75.75 0 01-.75-.75v-.008Z',
    hidden: true, // FWS v1.0: admin workbench, reachable from Settings rather than daily navigation
    component: lazy(() => import('../modules/hangar/Hangar')),
  },
  {
    key: 'incompletes',
    label: 'Incompletes',
    icon: 'ICONS.dashboard',
    hidden: true,
    component: lazy(() => import('../modules/dashboard/Incompletes')),
  },

  // ── BUSINESS DEVELOPMENT ────────────────────────────────────────────────
  // Real, Shane 2026-08-05: Business Development is a real nav GROUP label
  // only, never meant to be its own module or landing page -- both real
  // children (Lobby, HUNT) already have direct sidebar links. The prior
  // v1.7.39.205 decision hid this route but kept the component; real,
  // corrected now -- the route itself never should have existed. The group
  // label ('Business Development') stays completely untouched on Lobby/
  // HUNT's own entries below.
  {
    // v1.7.39.185 — real, direct sidebar link, Shane 2026-08-03: HUNT is a
    // real, everyday, top-of-the-day tool, not something that should need
    // an extra click through the Business Development hub every time.
    key: 'hunt',
    label: 'HUNT',
    icon: iconHunt,
    group: 'HUNT',
    // #1797 -- real, Stinger direct 2026-08-23: PROSPECTING cluster
    // (Hunt, Lobby). See Tech Sheets' comment for the full real
    // section-cluster explanation.
    sectionLabel: 'PROSPECTING',
    component: lazy(() => import('../modules/leadgen/ApolloSearch')), // HUNT is the page (internally the Prospector screen; the word Prospector is not shown to users)
    // HUNT is one flat left-nav item that opens the HUNT page (no one-child group, no caret). Its tabs: Search, Enrich Leads, Review Changes, Email Findings, Scout Activity, Rig Activity, Usage.
    // The old Prospector route (apollo-search) replace-redirects to it.
  },
  {
    // v1.7.39.197 — real GoldMine results page, Shane 2026-08-03: "the
    // real gold... the Gold Nuggets that come from this process" --
    // built as a results/log view of goldmine-inbound-cron.js's real
    // output, part of the HUNT family, reached via HuntLauncher tile.
    key: 'goldmine',
    label: 'GoldMine',
    icon: iconGoldmine,
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.GoldMineRedirect }))), // retired: now Prospector > Email Findings (file kept)
  },
  {
    // #2060 -- real, Goose direct 2026-09-08. UI piece on top of
    // Cougar's real, verified-live backend. Reuses #2059's DataTable
    // rowStatusColor pattern directly per instruction, not a new UI
    // system. Hidden top-level entry, real HUNT accordion subItem
    // above resolves here -- same convention as every other HUNT tool.
    key: 'field-intelligence',
    label: 'Field Intelligence',
    icon: 'ICONS.users',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.FieldIntelligenceRedirect }))), // retired: now Prospector > Rig Activity (file kept and embedded there)
  },
  {
    // v1.7.38 — CORRECTED: real file is bark/Scout.jsx, not leadgen/
    // MeetingScout.jsx (which never existed). Confirms the Bark→Scout
    // rename history logged 2026-07-22 — Bark wasn't dead, it became this.
    //
    // v1.7.39.187 — RENAMED, Shane 2026-08-03: "Scout" was being reused
    // for too many unrelated things, genuinely confusing. Scout is
    // reserved exclusively for the real capture tool (LinkedIn today,
    // other platforms later) going forward. This page is the real
    // account/prospect tracker — renamed to "Targets". Real, bigger
    // vision confirmed same session: Targets is the action layer on top
    // of Company Compass (top-down engineer a target list to fill real
    // discipline-hierarchy gaps at a company, sourced from Compass/Apollo
    // or Scout/LinkedIn) — not yet built, logged as its own item.
    key: 'targets',
    label: 'Targets',
    icon: 'ICONS.users',
    group: 'HUNT',
    hidden: true,
    // v1.7.39.74.36 — CORRECTED: real file moved to modules/hunt/Scout.jsx,
    // its logical home (Scout is the HUNT extension's account/prospect
    // tracker, live: 36 scout_accounts, 15 scout_contacts). Was still
    // sitting in modules/bark/ from before the Bark->Scout rename.
    // File itself still named Scout.jsx as of v1.7.39.187 — real,
    // deliberate scoped fix (nav label/key only, lower-risk); file rename
    // is a real, separate future follow-up.
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.TargetsRedirect }))), // retired: Top Targets widget on the dashboard (file kept)
  },
  {
    // Phase 8, 2026-09-10 -- real, new destination. Deliberately a NEW
    // key, not reusing 'targets' above -- that key is already wired to
    // Scout.jsx (a real, different, unrelated system), confirmed
    // before writing this. Purely additive.
    key: 'hunter-decisions',
    label: 'Hunter Decisions',
    icon: 'ICONS.users',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.HunterDecisionsRedirect }))), // retired (file kept)
  },
  {
    // v1.7.39.187 — RENAMED + WIRED, Shane 2026-08-03: was a real dead
    // stub under the confusing "Scout Tracker" name. Real fix: this slot
    // now hosts Whistle (real, built, previously completely unreachable
    // anywhere — the meeting-note capture tool). "Scout" reserved for the
    // capture tool only, going forward.
    key: 'whistle-hunt',
    label: 'Whistle',
    icon: 'ICONS.users',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/whistle/Whistle')),
  },
  {
    // v1.7.39.185 — real fix, Shane 2026-08-03: standing doctrine already
    // established tonight -- Apollo is vendor plumbing only, never
    // user-facing. Label was exposing it directly.
    // v1.7.39.190 — real, FINAL naming, Shane 2026-08-03: "Compass" ended
    // up belonging to the scoring systems (contact/company health), still
    // conceptually part of the HUNT family. This search/discovery tool is
    // Prospector -- complements Gold Mine's mining metaphor.
    key: 'apollo-search',
    label: 'HUNT',
    icon: 'ICONS.search',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.ProspectorRedirect }))), // retired name: the HUNT page
  },
  {
    key: 'prospector',
    label: 'HUNT',
    icon: 'ICONS.search',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.ProspectorRedirect }))), // /prospector and /hunt/prospector land on HUNT
  },
  {
    // v1.7.39.197 — real promotion, Shane 2026-08-03: Lobby gets the
    // same direct-link treatment HUNT got earlier this session --
    // "used constantly for daily triage," the extra click through
    // Business Development was real, unnecessary friction. Business
    // Development hub itself stays (Shane's explicit correction: it's
    // a real group, not just a wrapper for these two).
    key: 'lobby',
    // WO-0492 -- real, A51 direct 2026-09-15: "Lobby" retired as
    // primary user-facing terminology (reads as a holding-area/
    // graveyard; real model is CAPTURE->UNDERSTAND->DEVELOP->QUALIFY
    // ->ACT, not CAPTURE->PARK->WAIT). Label only -- key, component,
    // routes, file names, and the five-gate function underneath all
    // stay exactly as-is, per A51's explicit instruction not to
    // create unnecessary technical risk matching internal naming to
    // the UI. For the record (Stinger direct, 2026-09-15): official
    // name is "Lead Management", acronym CLMS (Charlie Lead
    // Management System).
    // #2305 (WO-CLMS-0, 2026-09-28, Stinger direct via Goose) -- REVERSAL
    // of the above, temporary label only, not a full revert of the CLMS
    // naming decision: this entry goes back to "Lobby" because a NEW,
    // separate "Lead Management" entry below is now the real CLMS rebuild
    // target. Everything the CLMS rebuild (WO-A/B/C/D) builds goes into
    // that new entry, not this one -- this one keeps its current
    // five-gate function under the old "Lobby" label until the new entry
    // is ready to replace it. Key/component/route untouched, same
    // discipline as the original WO-0492 rename.
    label: 'Lobby',
    icon: iconLobby,
    // v1.7.41.124 (#1507) -- real bug, Shane confirmed 2026-08-08:
    // breadcrumb read "C.H.A.R.L.I.E. / HUNT / Lobby", falsely nesting
    // Lobby under HUNT even though Lobby has no subItems relationship to
    // HUNT at all -- it's a fully independent top-level sidebar entry.
    // Root cause, confirmed live: this `group` field feeds ONLY the
    // breadcrumb (checked every real usage across src/ before touching
    // it) via App.js's `current?.group` lookup -- it does not affect
    // sidebar layout, accordion membership, or anything else. It was
    // simply set to 'HUNT' instead of 'Business Development', the real
    // conceptual parent this section's own header comment already
    // describes. Since no module has label 'Business Development' (the
    // BD route was deliberately removed, per that same comment --
    // "never meant to be its own module or landing page"), the
    // breadcrumb's groupLauncher lookup now cleanly finds nothing and
    // omits that segment entirely -- exactly the "own top-level module"
    // breadcrumb Shane expects, no new dummy route needed.
    group: 'Business Development',
    component: lazy(() => import('../modules/leadgen/Lobby')),
    hidden: true, // #2387: retired, redirects to Lead Management
  },
  {
    // #2305 (WO-CLMS-0, 2026-09-28, Stinger direct via Goose) -- NEW
    // entry, the real target for the CLMS rebuild (topgun-vault/projects/
    // clms-lead-management-rebuild-scope-v1.md). Placeholder only --
    // Part 1's actual functionality (qualified-contact gate, one-screen/
    // one-action Send Hello, source filtering, modern visual design) is
    // separate follow-on build work (WO-A/B/C/D), not this ticket. Sits
    // right after the renamed 'lobby' entry -- least disruptive
    // placement, matches this file's own established precedent for a
    // promoted/renamed item (see the 'hangar'/#2049 comment above).
    key: 'lead-management',
    label: 'Lead Management',
    icon: iconPipeline,
    group: 'Business Development',
    component: lazy(() => import('../modules/leadgen/LeadManagement')),
  },
  {
    // v1.7.39.208 -- real, Shane's ask 2026-08-04: The Ledger was
    // buried 3 levels deep (Settings > System & Data > The Ledger)
    // despite its own UI copy calling it "CHARLIE's real system of
    // record" and getting used constantly every real session. Same
    // real promotion treatment HUNT and Lobby already got. Reuses
    // reports.png (real dedicated icon commission on hold this
    // session).
    // v1.7.39.215 -- real, Shane's reversal 2026-08-05 (Side Notes Log
    // #3d/3B): moved back into Settings, this time under its own real
    // "Data Management" section (not buried in System & Data like
    // before) rather than sitting as a top-level main-nav item. Hidden
    // (not deleted) -- component/route stays reachable via Settings.
    key: 'the-ledger',
    label: 'Ledger',
    icon: iconReports,
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/settings/SystemHealthPage').then((m) => ({ default: m.systemHealthRoute('the-ledger') }))), // a tab of System Health now
  },
  {
    // v1.7.41.153 (#1453) -- real Bird Dog List. Shane's standing
    // concept 2026-08-06: companies spotted while driving, logged fast,
    // researched later. He confirmed HUNT as its home -- matches the
    // established real pattern (HUNT = discovery/prospecting tools).
    // Visible, not hidden: a capture tool that takes navigating into
    // Settings to reach will not get used from a truck.
    // #1797 -- real, Stinger direct 2026-08-23: moved into Hunt's
    // accordion as a real subItem. Entry stays, hidden, purely so
    // buildRouteMap() keeps resolving the real component -- same
    // convention as every other accordion child in this file.
    key: 'bird-dog-list',
    label: 'Bird Dog List',
    icon: 'ICONS.search',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.BirdDogListRedirect }))), // retired on desktop: Log a Sighting is under the + button (phone still opens the list)
  },
  {
    // v1.7.39.215 -- real, Shane's ask 2026-08-05 (Side Notes Log #3c):
    // Scout landing page, genuinely new -- not the same as "Targets"
    // (Scout's OLD name before the 2026-08-03 rename, a distinct real
    // thing today, the account/prospect tracker). Reached via HuntLauncher's
    // new Scout tile. Hosts the real Scout Scoreboard (embedded, moved
    // off its own standalone main-nav slot below).
    key: 'scout-landing',
    label: 'Scout',
    icon: 'ICONS.search',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.ScoutLandingRedirect }))), // retired: now Prospector > Scout Activity (file kept and embedded there)
  },
  {
    // v1.7.39.209 -- real, Shane's ask 2026-07-31 (real module, not a
    // widget). Real v1: shared, always-editable targets -- the
    // Shane-only-write / Alyssa-only-read split stays open.
    // v1.7.39.215 -- real, Shane's reversal 2026-08-05 (Side Notes Log
    // #3c): now embedded on the real, new Scout landing page instead
    // of standing alone in main nav. Hidden (not deleted) -- component/
    // route stays reachable directly if ever needed.
    key: 'scout-scoreboard',
    label: 'Scout Scoreboard',
    icon: iconReports,
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.ScoutScoreboardRedirect }))), // retired route (ScoutLanding still embeds the scoreboard)
  },
  {
    // GS-0037 -- GenServ Settings launcher (the GenServ shell's Settings). A tab of the GenServ page.
    key: 'genserv-settings',
    label: 'Settings',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-settings') }))),
  },
  {
    // GS-0168 -- Community Marketing and Advocates (development). A hidden tab of the GenServ page, reached from More > Business.
    key: 'genserv-growth',
    label: 'Growth',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-growth') }))),
  },
  {
    // GenServ field home -- today's board, reached from the phone's two-button home.
    key: 'genserv-today',
    label: 'Today’s Schedule',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-today') }))),
  },
  {
    // GenServ field home -- the Control Panel, one tap from the phone home.
    key: 'genserv-control-panel',
    label: 'Control Panel',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-control-panel') }))),
  },
  {
    // GS-0024 (#2506) -- GenServ More: the tools that are not everyday tabs. A tab of the GenServ page.
    key: 'genserv-more',
    label: 'More',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-more') }))),
  },
  {
    // GS-0011 (#2491) -- GenServ calls (placeholder). A tab of the GenServ page.
    key: 'genserv-calls',
    label: 'Appointment Builder',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-calls') }))),
  },
  {
    // #2513 -- GenServ Visit Report: complete the technical data of a finished visit from the phone. Opened from a Today stop, so not a tab.
    key: 'genserv-visit-report',
    label: 'Visit Report',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-visit-report') }))),
  },
  {
    // GS-0027 (#2509) -- GenServ New Job entry. A tab of the GenServ page; same builder component as Calls.
    key: 'genserv-new-job',
    label: 'New Job',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-new-job') }))),
  },
  {
    // GS-0001 (#2478) -- GenServ service schedule: every service_jobs visit by day. A tab of the GenServ page, same as the others.
    key: 'genserv-schedule',
    label: 'Schedule',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-schedule') }))),
  },
  {
    // GS-0020 (#2502) -- GenServ Outbox: every customer email waits here until a person approves it. A tab of the GenServ page.
    key: 'genserv-outbox',
    label: 'Messages',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-outbox') }))),
  },
  {
    // GS-0005 (#2483) -- GenServ customers: the CRM contacts scoped to GenServ (tag, service visit or generator). A tab of the GenServ page.
    key: 'genserv-customers',
    label: 'Customers',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-customers') }))),
  },
  {
    // GS-0088d -- GenServ portfolio dashboard and service point register. A tab of the GenServ page.
    key: 'genserv-register',
    label: 'Service Points',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-register') }))),
  },
  {
    // GS-0171 -- the previous Outbox screen, kept reachable while the redesigned Outbox (genserv-outbox) replaces it. Nothing was removed.
    key: 'genserv-outbox-classic',
    label: 'Outbox (Classic)',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-outbox-classic') }))),
  },
  {
    // GS-0096 -- GenServ Data Review: reviewed moves from notes into structured fields. A tab of the GenServ page.
    key: 'genserv-data-review',
    label: 'Data Review',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-data-review') }))),
  },
  {
    // GS-0108 -- Today's Route: the day's stops in order. Hidden, opened from the field home.
    key: 'genserv-route',
    label: 'Today’s Route',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-route') }))),
  },
  {
    // GS-0099 -- after Finish, the next scheduled stop of the day. Hidden, opened from the field home.
    key: 'genserv-next-stop',
    label: 'Next Stop',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-next-stop') }))),
  },
  {
    // GS-0100 -- add a stop picked up in the field. Hidden, opened from the field home.
    key: 'genserv-add-stop',
    label: 'Add Stop',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-add-stop') }))),
  },
  {
    // GS-0102 -- start from today's schedule or search any customer. Hidden, opened from the field home.
    key: 'genserv-start-job',
    label: 'Start a Job',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-start-job') }))),
  },
  {
    // v1.7.41.107 (Ledger #1602) -- real GenServ field wizard. Hidden,
    // not tied to a sidebar group -- this is a real standalone field
    // tool a technician opens directly on a phone at a job site, not
    // something navigated to through the app shell. Deep-linkable via
    // the real hash-routing work already shipped (#1430, same
    // session) if a direct URL is ever needed.
    key: 'genserv-field-wizard',
    label: 'Field Wizard',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-field-wizard') }))),
  },
  {
    // v1.7.41.109 (Ledger #1603) -- real report review/completion
    // screen. Hidden, same real reasoning as the field wizard --
    // staff opens this directly to build a report, not through
    // sidebar navigation.
    key: 'genserv-report-generator',
    label: 'Report Generator',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-report-generator') }))),
  },
  {
    // GS-0172: the previous Reports page, kept reachable while the new Reports workspace (genserv-reports) replaces it. Nothing was removed.
    key: 'genserv-reports-classic',
    label: 'Reports (Classic)',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-reports-classic') }))),
  },
  {
    // v1.7.41.125 (#1595) -- real, Shane's ask: bulk-load the
    // generator photo backlog. Hidden, same real reasoning as the
    // other two GenServ tools -- this is Stinger's own admin task
    // (a one-time-ish backlog load, then ongoing categorization), not
    // something that needs a permanent sidebar slot.
    key: 'genserv-photo-library',
    label: 'Photo Backlog',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-photo-library') }))),
  },
  {
    // v1.7.41.223 (Ledger #1753, Part 2) -- real job cost worksheet.
    // Hidden, same reasoning as the other GenServ tools -- Shane/Noah
    // open this directly before quoting a job, not through sidebar nav.
    key: 'genserv-job-cost-worksheet',
    label: 'Job Cost Worksheet',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-job-cost-worksheet') }))),
  },
  {
    // GS-0017 (#2499) -- Annual PM Plan and Recommissioning quote drafts. Hidden, same as the other GenServ tools; opens from GenServ > More.
    key: 'genserv-new-quote',
    label: 'New Quote',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-new-quote') }))),
  },
  {
    // v1.7.41.208 (Ledger #1753, Part 3) -- real GenServ paystub.
    // Companion to the Job Cost Worksheet: that one is forward-looking
    // (what should this job earn, before quoting), this one is
    // backward-looking (what did Noah actually earn on work already
    // performed, off real logged mileage and receipts). Hidden, same
    // reasoning as the other GenServ tools -- Shane opens this
    // directly when it's time to pay, not through sidebar nav.
    key: 'genserv-paystub',
    label: 'Paystub',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-paystub') }))),
  },
  {
    // v1.7.41.225 (Ledger #1787) -- real coupon tracking/issuing
    // screen. Hidden, same reasoning as the other GenServ tools.
    key: 'genserv-coupons',
    label: 'Coupons',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-coupons') }))),
  },
  {
    // v1.7.39.209 -- real, same fix pattern as The Ledger (Shane's
    // ask 2026-08-04, real combo pass). Was buried in Settings >
    // System & Data despite being a real pipeline stage (Scout ->
    // Enrichment House -> Lobby). Reuses the same real icon as
    // Reports for now (dedicated icon commission on hold).
    // v1.7.39.215 -- real, Shane's reversal 2026-08-05 (Side Notes
    // Log #3a): already correctly represented inside HUNT's own
    // landing page copy, top-level main-nav entry was redundant.
    // Hidden (not deleted) -- component/route stays reachable.
    key: 'enrichment-house',
    label: 'Enrichment House',
    icon: iconReports,
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.EnrichmentHouseRedirect }))), // retired (file kept)
  },
  {
    // v1.7.41.53 (Ledger #1052) -- real staging review for LinkedIn
    // export imports. Reachable via HUNT's sidebar accordion and the
    // HuntLauncher tile, same pattern as every other hidden HUNT entry
    // here. Uses the same generic search icon key as scout-landing's
    // own hidden entry above -- a dedicated icon is Iceman's call, not
    // mine (same discipline as #1508). HuntLauncher.jsx's tile (a
    // separate file, its own icon convention) uses the real iconScout
    // image asset since LinkedIn is the shared source there.
    key: 'linkedin-staging',
    label: 'LinkedIn Staging',
    icon: 'ICONS.search',
    group: 'HUNT',
    hidden: true,
    component: lazy(() => import('../modules/hunt/redirects').then((m) => ({ default: m.LinkedInStagingRedirect }))), // retired (file kept)
  },

  // ── CRM ─────────────────────────────────────────────────────────
  // v1.7.39.74 — CRM landing-page pilot. Shane's complaint: the sidebar
  // itself is crammed and keeps needing double-header workarounds every
  // time a group grows (Storage/Partners, then Contacts & Companies).
  // Same fix HUNT already proved out in Business Development: ONE
  // visible sidebar row per group, pointing to a landing page
  // (CrmLauncher.jsx) with tiles for everything underneath. Individual
  // pages stay fully routable (hidden: true, not removed) so deep
  // links, onNavigate calls elsewhere in the app, and browser history
  // all keep working exactly as before -- only the sidebar's own
  // rendering changes. (Outreach is retired as a name: Compose, Templates
  // and Workflows are their own screens with tiles on the CRM landing page.)
  // If this pilot holds up, Opportunity Management/Business
  // Development/Settings get the same treatment next.
  {
    key: 'crm',
    label: 'CRM',
    icon: iconCrm,
    group: 'CRM',
    // #1797 -- real, Stinger direct 2026-08-23: SALES cluster (CRM,
    // Sales). Label stays "CRM" (acronym), not sentence-cased --
    // Goose's correction, ticket text confirmed this explicitly.
    sectionLabel: 'SALES',
    component: lazy(() => import('../modules/crm/CrmLauncher')),
    // Sidebar children: Contacts, Companies, Tasks, Compose, Gifting. Templates, Workflows and Bounce
    // Management are tiles on the CRM landing page, not a second accordion level.
    subItems: [
      { key: 'contacts', label: 'Contacts' },
      { key: 'companies', label: 'Companies' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'compose', label: 'Compose' },
      // WO-0492 Phase 1 patch (2026-09-15) -- real, Gifting nested here
      // per A51 adjudication ("Gifting=CRM"), matching this same
      // one-level tile-list convention, not a second accordion level.
      { key: 'appreciation-gifting', label: 'Gifting' },
    ],
  },
  {
    key: 'contacts',
    label: 'Contacts',
    icon: 'ICONS.users',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/crm/Contacts')),
  },
  {
    key: 'companies',
    label: 'Companies',
    icon: 'ICONS.building',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/crm/Companies')),
  },
  {
    key: 'tasks',
    label: 'Tasks',
    icon: 'ICONS.check',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/tasks/Tasks')),
  },
  {
    key: 'outreach',
    label: 'Compose',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/redirects').then((m) => ({ default: m.OutreachRedirect }))), // retired name: Compose, Templates and Workflows are their own screens
  },
  {
    key: 'compose',
    label: 'Compose',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/Compose').then((m) => ({ default: withCrmPage(m.default, 'Compose') }))),
  },
  {
    key: 'templates',
    label: 'Templates',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/TemplatesChannels').then((m) => ({ default: withCrmPage(m.default, 'Templates') }))), // Email templates and the Text channel (SMS snippets)
  },
  {
    key: 'sequences',
    label: 'Sequences',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/redirects').then((m) => ({ default: m.SequencesRedirect }))), // retired route (see outreach/redirects.jsx)
  },
  {
    key: 'sms-snippets',
    label: 'SMS Snippets',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/redirects').then((m) => ({ default: m.SmsSnippetsRedirect }))), // retired route (see outreach/redirects.jsx)
  },
  {
    key: 'workflows',
    label: 'Workflows',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/Workflows').then((m) => ({ default: withCrmPage(m.default) }))),
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/redirects').then((m) => ({ default: m.AnalyticsRedirect }))), // retired route (see outreach/redirects.jsx)
  },
  {
    key: 'processor-health',
    label: 'Processor Health',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/redirects').then((m) => ({ default: m.ProcessorHealthRedirect }))), // retired route (see outreach/redirects.jsx)
  },
  {
    key: 'bounce-management',
    label: 'Bounce Management',
    icon: 'ICONS.mail',
    group: 'CRM',
    hidden: true,
    component: lazy(() => import('../modules/outreach/redirects').then((m) => ({ default: m.BounceManagementRedirect }))), // retired route: Bounces live in Lead Management (see outreach/redirects.jsx)
  },

  // ── SALES ────────────────────────────────────────────────────────────────
  // v1.7.39.154 — renamed group "Opportunity Management" -> "Sales" per
  // Shane ("too long, needs to become Sales again"). Real care taken:
  // the parent item's own label was ALSO "Opportunity Management" --
  // renaming both to "Sales" would have recreated the exact double-
  // header collision already fixed once for CRM (group label ==
  // item's own label). Same fix pattern reused: parent item gets its
  // own distinct label ("Overview"), group gets the short name.
  {
    // v1.7.41.135 (#1297 Part 1) -- real, dedicated Gifting view.
    // WO-0492 Phase 1 patch (2026-09-15) -- real, hidden:true added so
    // this renders as a genuine CRM subItem (accordion-nested, see
    // crm.subItems below) instead of a flat top-level SALES-cluster
    // sibling. buildRouteMap() still resolves 'appreciation-gifting'
    // correctly regardless of hidden status, same convention every
    // other hidden-but-routable entry in this file already uses.
    key: 'appreciation-gifting',
    label: 'Gifting',
    icon: iconSettings,
    group: 'Appreciation',
    hidden: true,
    component: lazy(() => import('../modules/appreciation/Gifting').then((m) => ({ default: withCrmPage(m.default) }))),
  },
  {
    // v1.7.41.146 (#1532) -- real, human-reviewed partner auto-connect
    key: 'partner-auto-connect',
    label: 'Partner Auto-Connect',
    icon: iconSettings,
    group: 'Settings',
    hidden: true,
    component: lazy(() => import('../modules/settings/PartnerAutoConnect')),
  },
  {
    key: 'opportunity-management',
    label: 'Sales',
    group: 'Sales',
    // Sales now opens directly into the operational Sales Desk. The launcher
    // duplicated the left navigation and added an unnecessary landing step.
    component: lazy(() => import('../modules/sales/SalesDesk')),
    // v1.7.40.37 — real, link-for-link match to
    // OpportunityManagementLauncher.jsx's own 6 tiles, same order.
    subItems: [
      // Real fix (#1870), Stinger direct: "move The Rig to the top of
      // the Sales sidebar accordion." Reordered above Quick Quote
      // (#1879) -- that entry is a separate fast-access shortcut into
      // the same real screen, not a substitute for the Sales Desk
      // itself being first in the list, which is the literal, real
      // ask here. Renamed The Rig -> Sales Desk 2026-08-30 (#1832,
      // Stinger direct); key 'quote-to-invoice' deliberately
      // unchanged so no route, deep-link or handler moves.
      { key: 'quote-to-invoice', label: 'Sales Desk' },
      { key: 'deals', label: 'Pipeline' },
      { key: 'product-catalog', label: 'Catalog' },
      // Real (#1890), Stinger direct, URGENT/TODAY.
      // Real, Stinger direct 2026-08-25: the real single-screen
      // consolidation he actually asked for in the "philosophical
      // debate" -- one list, one detail panel across Quote/SO/WO/
      // Invoice instead of 4 separate module routes. Replaces those
      // 4 real subItems with one. NOT touching the top-level Sales
      // landing component this time -- that stays the tile grid per
      // his explicit instruction, real change only, no repeat
      // guessing.
      { key: 'sales-log', label: 'Activity' },
    ],
  },
  {
    // GenServ: its own item on the left menu (and the phone More list). One page, one tab per GenServ screen; Today first. The count badge is drawn by App.js.
    key: 'genserv',
    label: 'GenServ',
    icon: 'M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.913-.143z',
    component: lazy(() => import('../modules/genserv/GenServ')),
  },
  {
    // Maintenance (the Maintenance Due list). Keeps its old Settings address.
    key: 'genserv-maintenance-due',
    label: 'Maintenance',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-maintenance-due') }))),
  },
  {
    // GS-0032 (#2514) -- GenServ Reports: one row per completed visit, one-click report, receipt and queued email. Hidden like the other GenServ tabs.
    key: 'genserv-reports',
    label: 'Reports',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-reports') }))),
  },
  {
    // GS-0034 (#2516) -- GenServ Finance: expense entry, GenServ expenses, possible GenServ expenses (job costing and profitability follow).
    key: 'genserv-finance',
    label: 'Finance',
    icon: 'ICONS.search',
    group: 'GenServ',
    hidden: true,
    component: lazy(() => import('../modules/genserv/GenServ').then((m) => ({ default: m.genservRoute('genserv-finance') }))),
  },
  {
    key: 'deals',
    label: 'Pipeline',
    icon: 'ICONS.box',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/Deals')),
  },
  {
    key: 'product-catalog',
    label: 'Catalog',
    icon: 'ICONS.box',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/CatalogPage')),
  },
  {
    // Real (#1890), Stinger direct, URGENT/TODAY: same real
    // registration pattern as product-catalog above -- reachable via
    // the Sales sidebar accordion, not a separate top-level nav item.
    key: 'phoenix-pricebook',
    label: 'Phoenix Price Book',
    icon: 'ICONS.box',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/CatalogPage').then((m) => ({ default: m.catalogRoute('phoenix-pricebook') }))), // a tab of Catalog now
  },
  {
    key: 'quotes',
    label: 'Quotes',
    icon: 'ICONS.file',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/SalesDeskRoutes').then((m) => ({ default: m.salesDeskStage('quote') }))), // a tab of Sales Desk now
  },
  {
    key: 'sales-orders',
    label: 'Sales Orders',
    icon: 'ICONS.clipboard',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/SalesDeskRoutes').then((m) => ({ default: m.salesDeskStage('sales_order') }))), // a tab of Sales Desk now
  },
  {
    key: 'work-orders',
    label: 'Work Orders',
    icon: 'ICONS.clipboard',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/SalesDeskRoutes').then((m) => ({ default: m.salesDeskStage('work_order') }))), // a tab of Sales Desk now
  },
  {
    key: 'invoices',
    label: 'Invoices',
    icon: 'ICONS.file',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/SalesDeskRoutes').then((m) => ({ default: m.salesDeskStage('invoice') }))), // a tab of Sales Desk now
  },
  {
    // Real, Stinger direct 2026-08-25: the real single-screen
    // consolidation -- one list, one detail panel across all 4
    // stages, real inline conversion, no navigating between separate
    // routes. The 4 original entries above stay real and intact
    // (hidden) -- the Deal Workspace panel in Deals.jsx still
    // navigates to those exact keys, and this is additive, not a
    // replacement.
    key: 'quote-to-invoice',
    label: 'Sales Desk',
    icon: 'ICONS.file',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/SalesDesk')),
  },
  {
    // v1.7.39.207 -- real Sales Log, Shane's ask 2026-08-04: "would be
    // nice to start a pipe market sales log to see the trends." Built
    // general-purpose (scoped to whichever partner is active, not
    // hardcoded to Pipe Market) per the Amenities Rule. Reuses the
    // existing sales_orders table (Ranch Rule), no parallel table.
    // Real, honest scope note: partner-scoped MODULE VISIBILITY isn't
    // a real capability yet -- reachable like any other Sales-family
    // page for now, real data always scoped to the active partner.
    key: 'sales-log',
    label: 'Activity',
    icon: 'ICONS.chart',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/ActivityPage')),
  },
  {
    // v1.7.39.208 -- real, Shane's ask 2026-08-04, "revive the tracker
    // concept". Named Tracker, not Targets, to avoid colliding with
    // the existing, different, unbuilt 'targets' key below.
    key: 'tracker',
    label: 'Tracker',
    icon: 'ICONS.target',
    group: 'Sales',
    hidden: true,
    component: lazy(() => import('../modules/sales/ActivityPage').then((m) => ({ default: m.activityRoute('tracker') }))), // a tab of Activity now
  },

  // ── KNOWLEDGE ────────────────────────────────────────────────────────────
  // v1.7.39.208 -- real, Shane's call 2026-08-04: Tech Sheets is real
  // sales-enablement material (pulled up mid-conversation or attached
  // to a quote), not analytics (Reports) or general storage. Moved
  // here from its old spot between Reports and Storage to match how
  // it actually gets used.
  {
    key: 'tech-sheets',
    label: 'Tech Sheets',
    icon: iconTechSheets,
    group: 'Knowledge',
    // #1797 -- real, Stinger direct 2026-08-23: five labeled section
    // clusters with hairline dividers, live in App.js's sidebar render
    // via this sectionLabel field on the first item of each cluster.
    // Dashboard stays ungrouped at top; Gifting/Settings stay
    // ungrouped at bottom -- matches the approved mockup exactly.
    // REPD is not mentioned anywhere in #1797's cluster spec -- left
    // ungrouped in its existing relative position rather than guessed
    // into REFERENCE or REPORTING, flagged on the ticket for Stinger
    // to confirm rather than assumed here.
    component: lazy(() => import('../modules/techsheets/TechSheets')),
  },
  {
    key: 'repd-launcher',
    label: 'REPD',
    icon: iconRepd,
    group: 'REPD',
    // WO-1834 (2026-09-29) -- real, Shane direct: RepD's landing page IS
    // the Overview content directly now, not a tile-grid launcher
    // requiring an extra click. RepDLauncher.jsx (the old tile grid)
    // is no longer wired in here -- file left in place, just unused,
    // same caution as every other retirement on this ticket.
    component: lazy(() => import('../modules/repd/RepD')),
    // Commission Tracking removed from RepD's own subItems -- folded
    // into Partner Billing per Shane direct (same underlying
    // commission-report route/component stays fully intact and
    // independently reachable from Reports' own subItems below,
    // untouched -- only RepD's own navigation to it changed).
    subItems: [
      { key: 'intake', label: 'Intake' },
      { key: 'partner-billing', label: 'Partner Billing' },
    ],
  },
  {
    // Real, kept: RepDHubWidget.jsx (dashboard sandbox tile) navigates
    // via key 'repd' directly -- same component as 'repd-launcher'
    // above, just the real key other real code already depends on.
    key: 'repd',
    label: 'Overview',
    icon: 'ICONS.helpCircle',
    group: 'REPD',
    hidden: true,
    component: lazy(() => import('../modules/repd/RepD')),
  },
  {
    // v1.7.41.227 (Ledger #1834) -- real, Stinger direct: "way too
    // busy and way too complicated." Intake is the fast path:
    // unassigned items grouped by day, one-tap partner buttons for
    // the 10 real known-active partners.
    key: 'intake',
    label: 'Intake',
    icon: 'ICONS.helpCircle',
    group: 'REPD',
    hidden: true,
    component: lazy(() => import('../modules/repd/Intake')),
  },
  {
    // WO-1834 (2026-09-29) -- real, full retirement of the standalone
    // Mileage/Transactions routes (both keys removed entirely, not just
    // hidden -- Shane direct: never independently reachable again).
    // Their real review/classification content is composed inside this
    // one new consolidated screen instead, alongside the new partner
    // report generator.
    key: 'partner-billing',
    label: 'Partner Billing',
    icon: 'ICONS.helpCircle',
    group: 'REPD',
    hidden: true,
    component: lazy(() => import('../modules/repd/PartnerBilling')),
  },

  // ── REPORTS ──────────────────────────────────────────────────────────────
  // v1.7.39.74 — new group, item #2 on the build list. Two real reports,
  // both pulling from data built earlier this same session.
  {
    key: 'reports',
    label: 'Reports',
    icon: iconReports,
    group: 'Reports',
    // #1797 -- real, Stinger direct 2026-08-23: originally REPORTING
    // cluster (Reports, Browse Files). WO-0492 Phase 1 patch
    // (2026-09-15) -- real, sectionLabel REMOVED per A51 adjudication:
    // Reports/Browse Files/Codex move to the quiet-utility tier
    // (same unlabeled treatment as Storage/Codex/Settings), full-weight
    // header no longer applies.
    component: lazy(() => import('../modules/reports/ReportsHub')), // one tabbed page: Overview plus every report (the old report routes below redirect to its tabs)
  },
  {
    key: 'commission-report',
    label: 'Commission Report',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.CommissionRedirect }))), // retired route: now a Reports tab
  },
  {
    // v1.7.39.197 — real dedup, same audit: already a real tile on
    // ReportsLauncher.jsx, this separate visible row was a true
    // duplicate. Stays reachable exactly where it already was.
    key: 'expense-report',
    label: 'Expense Report',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.ExpenseRedirect }))), // retired route: now a Reports tab
  },
  {
    key: 'merger-history-report',
    label: 'Merger & Acquisition History',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.MergerHistoryRedirect }))), // retired route: now a Reports tab
  },
  {
    key: 'contact-growth-report',
    label: 'Contact Growth',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.ContactGrowthRedirect }))), // retired route: now a Reports tab
  },
  {
    key: 'discipline-health-report',
    label: 'Discipline Health Snapshot',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.DisciplineHealthRedirect }))), // retired route: now a Reports tab
  },
  {
    // v1.7.39.209 -- real, Shane's ask 2026-08-04: Scout usage/adoption,
    // real first building block for the bigger Tool Utilization Stats
    // module (not scoped/built yet).
    key: 'scout-utilization-report',
    label: 'Scout Utilization',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.ScoutUtilizationRedirect }))), // retired route: now a Reports tab
  },
  {
    // Real, #1383 (DECISION LOCKED, Shane 2026-08-07). The full
    // cross-tool module the ScoutUtilizationReport comment above
    // pointed at. Real scope: Scout/GoldMine/Targets -- the three
    // tools with a confirmed real usage-proxy. RepD/Prospector/
    // Whistle/CRM/Reports deliberately excluded, real reasons in the
    // file's own header comment.
    key: 'tool-utilization-stats',
    label: 'Tool Utilization',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/ToolUtilizationStats')),
  },
  {
    // Real, #1322 (MAJOR FEATURE, sole reserved focus of v1.7.47).
    // The real, live report builder engine -- not hidden, this is the
    // real deliverable, not a hand-coded one-off.
    // #1797 -- real, Stinger direct 2026-08-23: moved into Reports'
    // accordion as a real subItem. Entry stays, hidden, purely so
    // buildRouteMap() keeps resolving the real component.
    key: 'report-builder',
    label: 'Report Builder',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.ReportBuilderRedirect }))), // retired route: now a Reports tab
  },
  {
    key: 'payroll-report',
    label: 'Payroll Report',
    icon: 'ICONS.fileText',
    group: 'Reports',
    hidden: true,
    component: lazy(() => import('../modules/reports/redirects').then((m) => ({ default: m.PayrollRedirect }))), // retired route: now a Reports tab
  },

  // ── STORAGE ──────────────────────────────────────────────────────────────
  // unconfirmed (open question logged 2026-07-22: Shane isn't sure which of
  // the two module folders, storage/ or files/, is the current live one).
  // Using StubView until that's resolved rather than guessing again.
  // v1.7.38 #DBL-HEADER-FIX: renamed label "Storage" → "Browse Files" to
  // eliminate the double-header (group "Storage" + item "Storage").
  {
    key: 'storage',
    label: 'Browse Files',
    icon: iconStorage,
    group: 'Storage',
    // v1.7.40 (QB v18, 2026-08-07) — real, fixes a confirmed live bug:
    // this entry had subItems but no component of its own, so clicking
    // it just expanded the accordion while the main content stayed on
    // whatever page was showing before. Real landing page, same pattern
    // as HUNT/Settings.
    component: lazy(() => import('../modules/storage/BrowseFilesLauncher')),
    subItems: [
      // v1.7.39.158 — real Storage.jsx wired in, replacing StubView. This
      // real, complete component (partner-scoped files table + charlie-files
      // bucket, real folder management) was built but never actually
      // connected to the nav -- sat unused. Same real Buddy System pattern
      // as Mileage/ExpenseReport found and fixed the same session.
      { key: 'storage-images',     label: 'Images',     component: lazy(() => import('../modules/storage/Storage').then((m) => ({ default: () => <m.default defaultType="image" /> }))) },
      { key: 'storage-documents',  label: 'Documents',  component: lazy(() => import('../modules/storage/Storage').then((m) => ({ default: () => <m.default defaultType="document" /> }))) },
      { key: 'storage-videos',     label: 'Videos',     component: lazy(() => import('../modules/storage/Storage').then((m) => ({ default: () => <m.default defaultType="video" /> }))) },
      { key: 'storage-recordings', label: 'Recordings', component: lazy(() => import('../modules/storage/Storage').then((m) => ({ default: () => <m.default defaultType="recording" /> }))) },
    ],
  },

  // ── SETTINGS ─────────────────────────────────────────────────────────────
  // v1.7.39.74 — SettingsLauncher.jsx replaces the 4-cluster dropdown
  // pattern with a single directory page (see that file's header comment
  // for why this is a directory, not another nested hub level).
  {
    // v1.7.41.132 (#1053) -- THE CODEX. Real, unified knowledge base.
    // #1797 -- moved up from its old spot near Settings to sit beside
    // Tech Sheets, forming the real REFERENCE cluster (Stinger direct
    // 2026-08-23).
    key: 'codex',
    label: 'The Codex',
    icon: iconSettings,
    group: 'The Codex',
    component: lazy(() => import('../modules/codex/Codex')),
  },

  // ── REPD ─────────────────────────────────────────────────────────────────
  // v1.7.39.74 — renamed group from "Expenses" to "RepD" per Shane
  // 2026-07-28: RepD is the real group name, not a sub-item under a
  // differently-named bucket, and it's growing beyond a single page --
  // commission tracking, TripLog, TripLog transactions all belong here.
  // Only RepD.jsx actually exists right now (confirmed against the real
  // repd/ folder listing, not guessed) -- the other three are stubbed
  // with StubView, same honest-placeholder pattern already used for
  // Storage's not-yet-built pieces, not fabricated functionality.
  // v1.7.39.215 -- real, Shane's naming fix 2026-08-05 (Side Notes Log
  // #3e): "RepD" -> "REPD" (all caps), nav label and group.
  {
    // #1820 -- real, Stinger direct 2026-08-24: Settings was still
    // showing as its own top-level sidebar item even after UserMenu
    // Previously hidden:true (consolidation decision, UserMenu's own
    // Settings button already routed here via buildRouteMap()).
    // WO-0492 Phase 1 patch (2026-09-15) -- real, un-hidden per A51
    // adjudication: Settings placed in the quiet-global-utility tier
    // (with Storage/Codex), visible in primary sidebar nav. This is a
    // deliberate reversal of that prior hidden decision, not an
    // oversight -- UserMenu's Settings button still routes to the same
    // place, now just a second, real, quiet-tier entry point too.
    key: 'settings',
    label: 'Settings',
    icon: iconSettings,
    group: 'Settings',
    component: lazy(() => import('../modules/settings/SettingsLauncher')),
    subItems: [
      { key: 'hangar', label: 'The Hangar', component: lazy(() => import('../modules/hangar/Hangar')) },
    ],
  },
  {
    key: 'settings-connections',
    label: 'Connections',
    icon: iconSettings,
    group: 'Settings',
    hidden: true,
    subItems: [
      { key: 'email-connections',    label: 'Email Connections',    component: lazy(() => import('../modules/settings/EmailConnections')) },
      { key: 'calendar-connections', label: 'Calendar Connections', component: lazy(() => import('../modules/settings/CalendarConnections')) },
      { key: 'company-succession',   label: 'Company Succession',   component: lazy(() => import('../modules/settings/CompanySuccessionPage')) },
      { key: 'company-succession-moves', label: 'Moves Log', hidden: true, component: lazy(() => import('../modules/settings/CompanySuccessionPage').then((m) => ({ default: m.successionRoute('company-succession-moves') }))) },
      { key: 'sender-health',        label: 'Sender Health',        component: lazy(() => import('../modules/settings/SenderHealth')) },
      { key: 'builder-hoppers',      label: 'Builder Hoppers',      component: lazy(() => import('../modules/settings/BuilderHoppers')) },
      { key: 'voice-rules',          label: 'Voice Rules',          component: lazy(() => import('../modules/settings/VoiceRulesPage')) },
      { key: 'voice-rules-preview',  label: 'Which Rule Is Used', hidden: true, component: lazy(() => import('../modules/settings/VoiceRulesPage').then((m) => ({ default: m.voiceRulesRoute('voice-rules-preview') }))) },
      { key: 'voice-rules-report',   label: 'Phrase Performance', hidden: true, component: lazy(() => import('../modules/settings/VoiceRulesPage').then((m) => ({ default: m.voiceRulesRoute('voice-rules-report') }))) },
      { key: 'voice-rules-wording',  label: 'Wording Windows', hidden: true, component: lazy(() => import('../modules/settings/VoiceRulesPage').then((m) => ({ default: m.voiceRulesRoute('voice-rules-wording') }))) },
      { key: 'scout-pairing',        label: 'Scout and Devices',    component: lazy(() => import('../modules/settings/ScoutPairing')) },
      // #2678: where the Scout extension's one-click Connect lands (#/scout-connect?ext=...&state=...). Behind the normal sign-in.
      { key: 'scout-connect',        label: 'Connect Scout', hidden: true, component: lazy(() => import('../modules/settings/ScoutConnect')) },
      // v1.7.39.183 — real, confirmed 324-line admin panel, built 2026-06-22,
      // never wired to anything until now. Manages booking_settings (working
      // hours, session lengths, buffer, Teams flag), the public book.wellrepd.com
      // link, and a real booking_requests log.
      { key: 'booking-settings',     label: 'Booking', hidden: true, component: lazy(() => import('../modules/appointments/Booking')) },
    ],
  },
  {
    // v1.7.38 #DBL-HEADER-FIX: renamed label "Partners" → "Partner Management"
    // to eliminate the double-header (cluster "Partners" + subItem "Partners").
    key: 'settings-partners',
    label: 'Partner Management',
    icon: iconSettings,
    group: 'Settings',
    hidden: true,
    subItems: [
      { key: 'partners',        label: 'Partners',        component: lazy(() => import('../modules/settings/PartnersManager')) },
      { key: 'partner-profile', label: 'Partner Profile', component: lazy(() => import('../modules/settings/PartnerProfile')) },
      { key: 'ps-lines', label: 'P.S. Lines', component: lazy(() => import('../modules/settings/PsLinesManager')) },
    ],
  },
  {
    key: 'settings-system',
    label: 'System & Data',
    icon: iconSettings,
    group: 'Settings',
    hidden: true,
    subItems: [
      { key: 'data-health',     label: 'Data Quality', hidden: true, component: lazy(() => import('../modules/settings/SystemHealthPage').then((m) => ({ default: m.systemHealthRoute('data-health') }))) },
      { key: 'known-contact-review', label: 'Known Contact Review', component: lazy(() => import('../modules/settings/KnownContactReview')) },
      { key: 'dead-domain-cleanup', label: 'Dead Domains', hidden: true, component: lazy(() => import('../modules/settings/CompanySuccessionPage').then((m) => ({ default: withCrmPage(m.successionRoute('dead-domain-cleanup'), 'Company succession', 'When a company is acquired, merged or renamed, its people follow it.', { hideTitle: true }) }))) },
      // Ledger #1611 -- moved out of top-level nav per Stinger: this is a
      // data-management/configuration tool, not daily work. Defining an
      // archetype happens once per partner at onboarding.
      { key: 'archetypes', label: 'Partner Archetypes', component: lazy(() => import('../modules/settings/ArchetypeEditor')) },
      // v1.7.39.159 — Import Manager and Export Manager, real CRM-owned
      // components (src/modules/crm/) surfaced here in Settings, per
      // Shane's real hierarchy: Data Management belongs to CRM, but a user
      // looks for it in Settings. Two distinct sibling features, not one
      // joined component.
      // Import and Export is one page with two tabs; the two old keys stay as hidden routes that open their tab.
      { key: 'import-export',   label: 'Import and Export', component: lazy(() => import('../modules/settings/ImportExportPage')) },
      { key: 'import-manager',  label: 'Import', hidden: true, component: lazy(() => import('../modules/settings/ImportExportPage').then((m) => ({ default: m.importExportRoute('import-manager') }))) },
      { key: 'export-manager',  label: 'Export', hidden: true, component: lazy(() => import('../modules/settings/ImportExportPage').then((m) => ({ default: m.importExportRoute('export-manager') }))) },
      // System Health is one page with five tabs (Cron and Errors, App Errors, Data Quality, Change History, Ledger); the old keys stay as hidden routes that open their tab.
      { key: 'system-health',   label: 'System Health',   component: lazy(() => import('../modules/settings/SystemHealthPage')) },
      { key: 'app-errors',      label: 'App Errors', hidden: true, component: lazy(() => import('../modules/settings/SystemHealthPage').then((m) => ({ default: m.systemHealthRoute('app-errors') }))) },
      { key: 'flagged',         label: 'Flagged', hidden: true, component: lazy(() => import('../modules/settings/SystemHealthPage').then((m) => ({ default: m.systemHealthRoute('flagged') }))) },
      { key: 'ops-audit',       label: 'Change History', hidden: true, component: lazy(() => import('../modules/settings/SystemHealthPage').then((m) => ({ default: m.systemHealthRoute('ops-audit') }))) },
      // v1.7.39.116 — renamed 'Apollo Settings' to 'Integrations'. This page
      // stops being Apollo-specific: it becomes the control panel for every
      // enrichment provider. Route key left as 'apollo-settings' deliberately
      // -- changing it would break any saved links/bookmarks for zero gain.
      { key: 'apollo-settings', label: 'Integrations', component: lazy(() => import('../modules/settings/Integrations')) },
      // v1.7.39.209 -- real, Shane's ask: Merger Tracker (already real,
      // built -- lives in Companies via MergePicker, real history at
      // merger-history-report) wasn't represented here like the other
      // integrity tools. Real shortcut, reuses the existing report page.
      { key: 'merger-history-report', label: 'Merger Tracker' },
    ],
  },
  {
    key: 'settings-account',
    label: 'Account',
    icon: iconSettings,
    group: 'Settings',
    hidden: true,
    subItems: [
      { key: 'custom-properties', label: 'Properties',            component: lazy(() => import('../modules/settings/CustomProperties')) },
      // Email Settings is one page with two tabs; the two old keys stay as hidden routes that open the matching tab.
      { key: 'email-settings',    label: 'Email Settings',        component: lazy(() => import('../modules/settings/EmailSettings')) },
      { key: 'reply-parser',      label: 'Reply Parser', hidden: true, component: lazy(() => import('../modules/settings/EmailSettings').then((m) => ({ default: m.emailSettingsRoute('reply-parser') }))) },
      { key: 'signatures',        label: 'Signatures', hidden: true, component: lazy(() => import('../modules/settings/EmailSettings').then((m) => ({ default: m.emailSettingsRoute('signatures') }))) },
      { key: 'verse-hopper',      label: 'Verse Hopper',          component: lazy(() => import('../modules/settings/VerseHopper')) },
      { key: 'identity-profile',  label: 'My Identity',           component: lazy(() => import('../modules/settings/IdentityProfile')) },
      { key: 'my-preferences',    label: 'My Preferences',        component: lazy(() => import('../modules/settings/MyPreferences')) },
      { key: 'about',             label: 'About C.H.A.R.L.I.E.', component: lazy(() => import('../modules/settings/About')) },
      { key: 'team-members',      label: 'Team Members',          component: lazy(() => import('../modules/settings/UsersAdmin')) },
      { key: 'team-member-profile', label: 'Team Member', hidden: true, component: lazy(() => import('../modules/settings/TeamMemberProfile')) },
    ],
  },
];

export function buildRouteMap() {
  const map = {};
  for (const mod of MODULE_REGISTRY) {
    if (mod.component) map[mod.key] = mod.component;
    if (mod.subItems) {
      for (const sub of mod.subItems) {
        if (sub.component) map[sub.key] = sub.component;
      }
    }
  }
  return map;
}

